'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { observer } from 'mobx-react-lite';

import { MotitoDatePicker } from '@/components/MotitoDatePicker';
import SectionHead from '@/components/SectionHead';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useAuthStore } from '@/presentation/hooks/useStores';
import { InfoSchema } from '@/validations/GuestbookValidation';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from '@/components/ui/select';
import { EMPLOYMENTS, EMPLOYMENTS_STATUS } from '@/libs/utils';
import { Environment } from '@/libs/Environment';
import { useRouter } from 'next/navigation';
 

type Props = {
  index: number;
  totalProcess: number;
  onIndexChange: (index: number) => void;
};

const Info = observer((_props: Props) => {
  const authStore = useAuthStore();
  const router = useRouter();

  const finalInfoSchema = InfoSchema.pick({
    fullName: authStore.isFieldMissing('full_name') ? true : undefined,
    dateOfBirth: authStore.isFieldMissing('date_of_birth') ? true : undefined,
    gender: authStore.isFieldMissing('gender') ? true : undefined,
    maritalStatus: authStore.isFieldMissing('marital_status') ? true : undefined,
    employmentStatus: authStore.isFieldMissing('employment_status')
      ? true
      : undefined,
    occupation: true,
    company: authStore.isFieldMissing('company') ? true : undefined,
    digitalAddress: authStore.isFieldMissing('digital_address')
      ? true
      : undefined,
    salary: authStore.isFieldMissing('salary') ? true : undefined,
  });

  const infoForm = useForm<z.infer<typeof finalInfoSchema>>({
    resolver: zodResolver(finalInfoSchema),
    defaultValues: {
      fullName: authStore.registrationData?.fullName || '',
      dateOfBirth: authStore.registrationData?.dateOfBirth
        ? new Date(authStore.registrationData.dateOfBirth)
        : new Date(),
      gender: authStore.registrationData?.gender || '',
      maritalStatus: authStore.registrationData?.maritalStatus || '',
    },
  });

  function onSubmit(values: z.infer<typeof finalInfoSchema>) {
      // Update registration data in AuthStore
      authStore.updateRegistrationData({
        fullName: values.fullName,
        dateOfBirth: values.dateOfBirth?.toISOString(),
        gender: values.gender,
        maritalStatus: values.maritalStatus,
        employmentStatus: values.employmentStatus,
        occupation: values.occupation,
      });
      router.push('/sign-up/tnc');
    
  }

  return (
    <>
      <SectionHead
        title="Tells us about yourself"
        description="Provide more details about yourself to better understand and meet our KYC requirements."
      />

      <article className="w-full">
        <Form {...infoForm}>
          <form
            onSubmit={infoForm.handleSubmit(onSubmit)}
            className="flex w-full flex-col gap-8"
          >
            <FormItem className="flex flex-col gap-3">
              {authStore.isFieldMissing('full_name') && (
                <FormField
                  control={infoForm.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full name</FormLabel>
                      <FormControl>
                        <Input placeholder="eg. John Asante" {...field} />
                      </FormControl>
                      <FormDescription>
                        Provide name as shown on your ID cards
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {authStore.isFieldMissing('gender') && (
                <FormField
                  control={infoForm.control}
                  name="gender"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-4">
                      <FormLabel>Your Gender</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className="flex flex-col"
                        >
                          {Environment.gender.map((gender) => (
                            <FormItem
                              className="my-1 flex flex-row items-center"
                              key={gender}
                            >
                              <FormControl>
                                <RadioGroupItem value={gender.toLowerCase()} />
                              </FormControl>
                              <FormLabel className="font-normal text-[#3C3C3C]">
                                {gender}
                              </FormLabel>
                            </FormItem>
                          ))}
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {authStore.isFieldMissing('marital_status') && (
                <FormField
                  control={infoForm.control}
                  name="maritalStatus"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-4">
                      <FormLabel>Marital Status</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className="flex flex-col"
                        >
                          {Environment.status.map((status) => (
                            <FormItem
                              className="my-1 flex flex-row items-center"
                              key={status}
                            >
                              <FormControl>
                                <RadioGroupItem value={status} />
                              </FormControl>
                              <FormLabel className="font-normal text-[#3C3C3C]">
                                {status}
                              </FormLabel>
                            </FormItem>
                          ))}
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              {authStore.isFieldMissing('date_of_birth') && (
                <FormField
                  control={infoForm.control}
                  name="dateOfBirth"
                  render={({ field }) => <MotitoDatePicker {...field} />}
                />
              )}

            { authStore.isFieldMissing('employment_status') && (
              <FormField
              control={infoForm.control}
              name="employmentStatus"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Employment Status</FormLabel>
                  <FormControl>
                    <Select
                      {...field}
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select your employment status" />
                      </SelectTrigger>
                      <SelectContent>
                        {EMPLOYMENTS_STATUS.map((status) => (
                          <SelectItem
                            key={status}
                            value={status}
                            className="cursor-pointer hover:bg-motitoBlue/10"
                          >
                            {status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  {/* <FormDescription>
                        This is your public display name.
                      </FormDescription> */}
                  <FormMessage />
                </FormItem>
              )}
            />
            )}

            <FormField
              control={infoForm.control}
              name="occupation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Occupation</FormLabel>
                  <FormControl>
                    <Select
                      {...field}
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select your occupation" />
                      </SelectTrigger>
                      <SelectContent>
                        {EMPLOYMENTS.map((occupation) => (
                          <SelectItem
                            key={occupation}
                            value={occupation}
                            className="cursor-pointer hover:bg-motitoBlue/10"
                          >
                            {occupation}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  {/* <FormDescription>
                        This is your public display name.
                      </FormDescription> */}
                  <FormMessage />
                </FormItem>
              )}
            />
            </FormItem>
            <Button
              className=" mt-3 w-full rounded-3xl bg-motitoBlue py-3 text-white hover:bg-blue-700"
              loading={authStore.isUpdatingRegistration}
              disabled={authStore.isUpdatingRegistration}
            >
              Continue
            </Button>
          </form>
        </Form>
      </article>
    </>
  );
});

export default Info;
